/* USAMCA recolor engine — vanilla port of the locked RTA visualizer math
 * (paint: luminance-preserving remap LP/HI/FLOOR; stain: species base tone +
 * translucent tint over the door's own grain). Rule: no mask, no recolor —
 * the original photo always stands.
 * Assets (masks, photos, data) come CORS-clean from the opcabinets origin.
 */
window.UsamcaRecolor = (function () {
  'use strict';

  var LP = 0.84, HI = 0.18, FLOOR = 0.40;
  var FINISH_KEY = 'usamca_finish';

  function hexToRgb(hex) {
    var h = String(hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var v = parseInt(h, 16) || 0;
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  }

  function loadImage(src) {
    return new Promise(function (res, rej) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { res(img); };
      img.onerror = function () { console.error('[usamca] image failed to load:', src); rej(new Error('img ' + src)); };
      img.src = src;
    });
  }

  var manifestP = null, stainsP = null;
  function manifest(apiBase) {
    if (!manifestP) {
      manifestP = fetch(apiBase + '/door-masks/manifest.json')
        .then(function (r) {
          if (!r.ok) { console.error('[usamca] manifest fetch failed:', r.status); return {}; }
          return r.json();
        })
        .then(function (m) { window.__usamcaManifestKeys = Object.keys(m).length; return m; })
        .catch(function (e) { console.error('[usamca] manifest fetch error:', e); window.__usamcaManifestKeys = -1; return {}; });
    }
    return manifestP;
  }
  function stainData(apiBase) {
    if (!stainsP) {
      stainsP = fetch(apiBase + '/api/v1/usamca/stain-colors')
        .then(function (r) {
          if (!r.ok) { console.error('[usamca] stain-colors fetch failed:', r.status); return { stains: {}, speciesTones: {} }; }
          return r.json();
        })
        .catch(function (e) { console.error('[usamca] stain-colors fetch error:', e); return { stains: {}, speciesTones: {} }; });
    }
    return stainsP;
  }

  function maskKey(src) {
    var name = src.split('?')[0].split('/').pop() || '';
    return name.replace(/\.(webp|png|jpe?g|gif|svg)$/i, '');
  }

  function segment(img, maskImg, size) {
    var maxDim = size || 760;
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    var scale = Math.min(1, maxDim / Math.max(iw, ih));
    var W = Math.max(1, Math.round(iw * scale)), H = Math.max(1, Math.round(ih * scale)), N = W * H;
    var pc = document.createElement('canvas'); pc.width = W; pc.height = H;
    var pcx = pc.getContext('2d', { willReadFrequently: true });
    pcx.drawImage(img, 0, 0, W, H);
    var d = pcx.getImageData(0, 0, W, H).data;
    var mc = document.createElement('canvas'); mc.width = W; mc.height = H;
    var mcx = mc.getContext('2d', { willReadFrequently: true });
    mcx.drawImage(maskImg, 0, 0, W, H);
    var md = mcx.getImageData(0, 0, W, H).data;
    var alpha = new Uint8Array(N), lum = new Float32Array(N);
    for (var i = 0; i < N; i++) {
      var k = i * 4;
      alpha[i] = md[k];
      lum[i] = (0.299 * d[k] + 0.587 * d[k + 1] + 0.114 * d[k + 2]) / 255;
    }
    return { W: W, H: H, N: N, alpha: alpha, lum: lum, base: new Uint8ClampedArray(d) };
  }

  function paintPx(L, col) {
    if (L <= LP) { var f = FLOOR + (1 - FLOOR) * (L / LP); return [col[0] * f, col[1] * f, col[2] * f]; }
    var t = Math.min((L - LP) / (1 - LP), 1) * HI;
    return [col[0] + (255 - col[0]) * t, col[1] + (255 - col[1]) * t, col[2] + (255 - col[2]) * t];
  }

  // Two-pass box blur over the luminance map. Paint mode uses this so wood
  // grain in the source photo doesn't bleed into a painted finish, while
  // large-scale shading (bevels, panel shadows) survives. Stain keeps raw lum.
  function smoothLum(seg) {
    if (seg.lumSmooth) return seg.lumSmooth;
    var W = seg.W, H = seg.H, src = seg.lum;
    var r = Math.max(2, Math.round(Math.max(W, H) / 120));
    var tmp = new Float32Array(W * H), out = new Float32Array(W * H);
    var x, y, i, acc, span = r * 2 + 1;
    for (y = 0; y < H; y++) {            // horizontal
      acc = 0;
      for (x = -r; x <= r; x++) acc += src[y * W + Math.min(W - 1, Math.max(0, x))];
      for (x = 0; x < W; x++) {
        tmp[y * W + x] = acc / span;
        var xo = Math.max(0, x - r), xi = Math.min(W - 1, x + r + 1);
        acc += src[y * W + xi] - src[y * W + xo];
      }
    }
    for (x = 0; x < W; x++) {            // vertical
      acc = 0;
      for (y = -r; y <= r; y++) acc += tmp[Math.min(H - 1, Math.max(0, y)) * W + x];
      for (y = 0; y < H; y++) {
        out[y * W + x] = acc / span;
        var yo = Math.max(0, y - r), yi = Math.min(H - 1, y + r + 1);
        acc += tmp[yi * W + x] - tmp[yo * W + x];
      }
    }
    seg.lumSmooth = out;
    return out;
  }

  // spec: {mode:'paint', color:'#hex'}
  //     | {mode:'stain', species:'#hex', stain:'#hex', strength:0..1}
  //     | {mode:'original'}
  function repaint(canvas, seg, spec) {
    var W = seg.W, H = seg.H, N = seg.N;
    canvas.width = W; canvas.height = H;
    var ctx = canvas.getContext('2d');
    var out = ctx.createImageData(W, H), o = out.data;
    if (!spec || spec.mode === 'original') { o.set(seg.base); ctx.putImageData(out, 0, 0); return; }
    var front = hexToRgb(spec.mode === 'paint' ? spec.color : spec.species);
    var tint = spec.mode === 'stain' ? hexToRgb(spec.stain) : null;
    var s = spec.mode === 'stain' ? (spec.strength || 0) : 0;
    var lum = spec.mode === 'paint' ? smoothLum(seg) : seg.lum;
    for (var i = 0; i < N; i++) {
      var k = i * 4, a = seg.alpha[i];
      if (a < 12) { o[k] = seg.base[k]; o[k + 1] = seg.base[k + 1]; o[k + 2] = seg.base[k + 2]; o[k + 3] = seg.base[k + 3]; continue; }
      var pp = paintPx(lum[i], front);
      var r = pp[0], g = pp[1], b = pp[2];
      if (tint) {
        var tp = paintPx(lum[i], tint);
        r += (tp[0] - r) * s; g += (tp[1] - g) * s; b += (tp[2] - b) * s;
      }
      var f = a / 255;
      o[k] = (r * f + seg.base[k] * (1 - f)) | 0;
      o[k + 1] = (g * f + seg.base[k + 1] * (1 - f)) | 0;
      o[k + 2] = (b * f + seg.base[k + 2] * (1 - f)) | 0;
      o[k + 3] = 255;
    }
    ctx.putImageData(out, 0, 0);
  }

  // Attach a live canvas behind an <img>. Returns a handle with setSpec();
  // until the mask + photo are ready (or if either fails) the photo stands.
  function attach(imgEl, apiBase) {
    var handle = { ready: false, seg: null, canvas: null, pending: null, setSpec: setSpec };
    function setSpec(spec) {
      handle.pending = spec;
      if (!handle.ready) return;
      if (!spec || spec.mode === 'original') {
        handle.canvas.style.display = 'none'; imgEl.style.display = '';
      } else {
        repaint(handle.canvas, handle.seg, spec);
        handle.canvas.style.display = ''; imgEl.style.display = 'none';
      }
    }
    manifest(apiBase).then(function (m) {
      var entry = m[maskKey(imgEl.getAttribute('data-recolor-src') || imgEl.src)];
      if (!entry || !entry.file) { console.error('[usamca] no mask for', maskKey(imgEl.getAttribute('data-recolor-src') || imgEl.src)); return; }
      var src = imgEl.getAttribute('data-recolor-src') || imgEl.src;
      return Promise.all([loadImage(src), loadImage(apiBase + '/door-masks/' + entry.file)])
        .then(function (pair) {
          handle.seg = segment(pair[0], pair[1]);
          var c = document.createElement('canvas');
          c.style.cssText = 'width:100%;height:auto;display:none';
          c.setAttribute('role', 'img');
          c.setAttribute('aria-label', imgEl.alt || '');
          imgEl.parentNode.insertBefore(c, imgEl.nextSibling);
          handle.canvas = c;
          handle.ready = true;
          if (handle.pending) setSpec(handle.pending);
        });
    }).catch(function (e) { console.error('[usamca] attach failed:', e && e.message ? e.message : e); });
    return handle;
  }

  function saveFinish(sel) { try { localStorage.setItem(FINISH_KEY, JSON.stringify(sel)); } catch (e) {} }
  function loadFinish() { try { return JSON.parse(localStorage.getItem(FINISH_KEY)); } catch (e) { return null; } }
  function clearFinish() { try { localStorage.removeItem(FINISH_KEY); } catch (e) {} }

  return {
    attach: attach, repaint: repaint, segment: segment, hexToRgb: hexToRgb,
    manifest: manifest, stainData: stainData, maskKey: maskKey,
    saveFinish: saveFinish, loadFinish: loadFinish, clearFinish: clearFinish,
    FINISH_KEY: FINISH_KEY
  };
})();
